import React, { useEffect, useRef } from 'react'
import { Phone } from 'lucide-react'
import RippleBackground from './RippleBackground'
import { Role } from '../types'

// Global flag to track if call has ended
declare global {
  var callEnded: boolean
}

// Initialize global flag
if (typeof window !== 'undefined') {
  (window as any).callEnded = false
}

interface DialingScreenProps {
  role: Role
  sessionId: string
  onCallStart: () => void
  onCallEnd: () => void
  backendWsRef: React.RefObject<WebSocket | null>
}

const DialingScreen: React.FC<DialingScreenProps> = ({
  role,
  sessionId,
  onCallStart,
  backendWsRef
}) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const connectionTimeoutRef = useRef<number | null>(null)
  const onCallStartRef = useRef(onCallStart)
  const connectionAttemptedRef = useRef<boolean>(false)
  const isCallEndedRef = useRef<boolean>(false)
  
  // Update ref when onCallStart changes
  useEffect(() => {
    onCallStartRef.current = onCallStart
  }, [onCallStart])

  // Audio handling is now done by CallInterface


  // Play dialog tone
  useEffect(() => {
    const playDialogTone = () => {
      if (audioRef.current) {
        audioRef.current.loop = true
        audioRef.current.play().catch((error) => {
          // Ignore AbortError which happens when component unmounts during play
          if (error.name !== 'AbortError') {
            console.error('Error playing dialog tone:', error)
          }
        })
      }
    }

    playDialogTone()

    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
    }
  }, [])

  // Wait 2 seconds then connect to backend WebSocket only
  useEffect(() => {
    // Prevent multiple connection attempts
    if (connectionAttemptedRef.current || backendWsRef.current) {
      console.log('Connection already attempted or exists, skipping...')
      return
    }
    
    const connectToBackend = async () => {
      connectionAttemptedRef.current = true
      console.log('DialingScreen: Starting connection attempt...')
      
      // Wait 2 seconds first
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      
      try {
        // Connect to backend WebSocket only
        const backendWs = new WebSocket(import.meta.env.VITE_BACKEND_WS_URL || 'wss://talkwithzayn.getgeek.ai/backend-ai/ws')
        backendWs.binaryType = 'arraybuffer' // Enable binary data handling
        // @ts-ignore - Assign to the mutable ref
        backendWsRef.current = backendWs
        
        // Wait for backend connection
        const backendConnected = new Promise((resolve, reject) => {
          backendWs.onopen = () => resolve(backendWs)
          backendWs.onerror = () => reject(new Error('Backend connection failed'))
        })
        
        // Wait for backend connection to succeed
        await backendConnected
        
        console.log('Backend WebSocket connected successfully')
        
        // Send start call message to backend
        backendWs.send(JSON.stringify({
          type: 'start_call',
          role: role.id,
          sessionId: sessionId,
          candidateName: 'there'
        }))
        
        // Wait for start_interview response
        backendWs.onmessage = (event) => {
          // Store audio data for CallInterface to play after transition
          if (event.data instanceof Blob || event.data instanceof ArrayBuffer) {
            console.log('Received binary audio data from backend in DialingScreen - storing for CallInterface')
            
            // Store audio data in a global variable for CallInterface to use
            if (!(window as any).pendingGreetingAudio) {
              ;(window as any).pendingGreetingAudio = []
              console.log('DialingScreen: Created new pendingGreetingAudio array')
            }
            
            if (event.data instanceof ArrayBuffer) {
              ;(window as any).pendingGreetingAudio.push(event.data)
              console.log(`DialingScreen: Added ArrayBuffer chunk, size: ${event.data.byteLength}, total chunks: ${(window as any).pendingGreetingAudio.length}`)
            } else if (event.data instanceof Blob) {
              // Convert Blob to ArrayBuffer
              event.data.arrayBuffer().then((arrayBuffer) => {
                ;(window as any).pendingGreetingAudio.push(arrayBuffer)
                console.log(`DialingScreen: Added Blob->ArrayBuffer chunk, size: ${arrayBuffer.byteLength}, total chunks: ${(window as any).pendingGreetingAudio.length}`)
              })
            }
            return
          }
          
          // Handle JSON messages
          try {
            const message = JSON.parse(event.data)
            console.log('Received message from backend:', message)
            
            if (message.type === 'interview_started') {
              console.log('Received interview_started response:', message.greeting)
              
              // Transition to CallInterface immediately when we receive the message
              // CallInterface will handle the audio playback
              if (!isCallEndedRef.current && !(window as any).callEnded) {
                console.log('Received interview_started, transitioning to call screen immediately...')
                onCallStartRef.current()
              } else {
                console.log('Call already ended, not transitioning to call screen')
              }
              
              // DON'T close the WebSocket - CallInterface will reuse it
              console.log('DialingScreen: Keeping WebSocket open for CallInterface to reuse')
            }
          } catch (error) {
            console.error('Error parsing backend message:', error)
            console.log('Raw data that failed to parse:', event.data)
          }
        }
        
      } catch (error) {
        console.error('Backend connection failed:', error)
        // Retry after 1 second
        connectionTimeoutRef.current = setTimeout(() => {
          connectToBackend()
        }, 1000)
      }
    }

    connectToBackend()

    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current)
      }
      // DON'T close the WebSocket connection - CallInterface will reuse it
      console.log('DialingScreen: Cleanup - keeping WebSocket open for CallInterface')
    }
  }, []) // Remove role.id dependency to prevent re-runs


  return (
    <div className="min-h-screen w-screen flex flex-col items-center justify-between relative overflow-hidden p-4 sm:p-8 md:p-16">
      {/* Background Video */}
      <video 
        autoPlay 
        loop 
        muted 
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="https://pub-b4a0d7160e804448b1c1062c499df6e5.r2.dev/background.mp4" type="video/mp4" />
      </video>
      
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/20"></div>
      
      {/* Background concentric circles - shared */}
      <RippleBackground anchorToParentCenter />

      {/* Top section - Role title and Timer */}
      <div className="relative z-10 flex flex-col items-center space-y-4 sm:space-y-6 md:space-y-8">
        {/* Role title */}
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold text-white text-center">{role.title}</h1>
        </div>

        {/* Timer */}
        <div>
          <p className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white">2:00</p>
        </div>
      </div>

      {/* Central logo - positioned slightly above exact center for visual alignment */}
      <div className="relative z-10 flex items-center justify-center -mt-4 sm:-mt-5 md:-mt-6">
        <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 bg-white rounded-full flex items-center justify-center shadow-2xl">
          <img 
            src="https://public-getgeek-s3-bucket-storage.s3.amazonaws.com/qrs/zayn/center_calling_logo.png" 
            alt="Zayn Logo" 
            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-contain"
          />
        </div>
      </div>

      {/* Bottom section - Calling button */}
      <div className="relative z-10">
        <button 
          disabled
          className="text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full flex items-center space-x-2 sm:space-x-3 shadow-lg cursor-default"
          style={{ backgroundColor: '#5DCD19' }}
        >
          <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
          <span className="text-base sm:text-lg font-medium">Calling...</span>
        </button>
      </div>

      {/* Hidden audio element for dialog tone */}
      <audio
        ref={audioRef}
        preload="auto"
        className="hidden"
      >
        <source src="/dialog-tone.mp3" type="audio/mpeg" />
        <source src="/dialog-tone.wav" type="audio/wav" />
      </audio>
    </div>
  )
}

export default DialingScreen
